import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  getComplaints, acknowledgeComplaint, addComplaintCorrespondence,
  resolveComplaint, escalateComplaintToIncident,
  getSeverityBadge, timeUntilDeadline,
} from '../../services/complianceService';
import toast from 'react-hot-toast';

export default function AdminComplaints() {
  const navigate = useNavigate();
  const [complaints, setComplaints] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState({ status: '', severity: '' });
  const [selected, setSelected] = useState(null);
  const [responseBody, setResponseBody] = useState('');
  const [resolution, setResolution] = useState('');
  const [actionInProgress, setActionInProgress] = useState(false);

  const load = () => {
    setLoading(true);
    getComplaints(filter)
      .then((res) => setComplaints(res.complaints || []))
      .catch((err) => toast.error(err.message || 'Could not load'))
      .finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, [filter.status, filter.severity]);

  const handleAck = async () => {
    setActionInProgress(true);
    try { await acknowledgeComplaint(selected.id); toast.success('Acknowledged'); load(); setSelected(null); }
    catch (err) { toast.error(err.message); }
    setActionInProgress(false);
  };

  const handleRespond = async () => {
    if (!responseBody || responseBody.length < 5) { toast.error('Response too short'); return; }
    setActionInProgress(true);
    try {
      await addComplaintCorrespondence(selected.id, 'outbound', responseBody);
      toast.success('Response sent');
      setResponseBody(''); load(); setSelected(null);
    } catch (err) { toast.error(err.message); }
    setActionInProgress(false);
  };

  const handleResolve = async () => {
    if (!resolution || resolution.length < 10) { toast.error('Resolution too short'); return; }
    setActionInProgress(true);
    try {
      await resolveComplaint(selected.id, resolution);
      toast.success('Resolved'); setResolution(''); load(); setSelected(null);
    } catch (err) { toast.error(err.message); }
    setActionInProgress(false);
  };

  return (
    <div className="max-w-5xl mx-auto p-6">
      <div className="mb-4">
        <button onClick={() => navigate('/admin')}
          className="bg-transparent border-0 text-brand-primary font-semibold text-[0.9rem] cursor-pointer p-0">
          ← Admin dashboard
        </button>
      </div>

      <h1 className="font-serif text-3xl text-brand-dark mb-2">Complaints</h1>
      <p className="text-body text-[0.9rem] mb-6">
        SLA: Acknowledge in 24hr, respond in 7 days, resolve in 28 days.
      </p>

      <div className="flex gap-2 mb-4 flex-wrap">
        <select className="form-input form-select flex-1 min-w-[150px]" value={filter.status}
          onChange={(e) => setFilter({ ...filter, status: e.target.value })}>
          <option value="">All statuses</option>
          <option value="submitted">Submitted (needs ack)</option>
          <option value="acknowledged">Acknowledged</option>
          <option value="investigating">Investigating</option>
          <option value="awaiting_complainant">Awaiting complainant</option>
          <option value="resolved">Resolved</option>
        </select>
        <select className="form-input form-select flex-1 min-w-[150px]" value={filter.severity}
          onChange={(e) => setFilter({ ...filter, severity: e.target.value })}>
          <option value="">All severities</option>
          <option value="critical">Critical</option>
          <option value="high">High</option>
          <option value="medium">Medium</option>
          <option value="low">Low</option>
        </select>
      </div>

      {loading && <div className="p-10 text-center text-muted">Loading...</div>}

      <div className="grid gap-3">
        {complaints.map((c) => {
          const sev = getSeverityBadge(c.severity);
          const ackDeadline = c.status === 'submitted' && c.acknowledgmentDeadline && timeUntilDeadline(c.acknowledgmentDeadline?._seconds ? new Date(c.acknowledgmentDeadline._seconds * 1000).toISOString() : c.acknowledgmentDeadline);
          return (
            <div key={c.id} onClick={() => setSelected(c)}
              className="p-4 rounded-[14px] bg-white border border-slate-200 cursor-pointer">
              <div className="flex justify-between items-start mb-1.5">
                <div>
                  <span className="font-bold text-brand-dark">{c.referenceNumber}</span>
                  <span className="ml-2 text-[0.85rem] text-body">{c.categoryLabel}</span>
                </div>
                <div className="flex gap-1.5">
                  <span className="px-2.5 py-[3px] rounded-full text-[0.7rem] font-bold"
                    style={{ background: sev.bg, color: sev.color }}>
                    {sev.label}
                  </span>
                  {ackDeadline && (
                    <span className="px-2.5 py-[3px] rounded-full text-[0.7rem] font-bold"
                      style={{
                        background: ackDeadline.overdue ? '#fee2e2' : '#fef3c7',
                        color: ackDeadline.overdue ? '#991b1b' : '#92400e',
                      }}>Ack: {ackDeadline.label}</span>
                  )}
                </div>
              </div>
              <div className="text-[0.85rem] text-body mb-1">
                {c.description.length > 150 ? c.description.substring(0, 150) + '...' : c.description}
              </div>
              <div className="flex justify-between text-[0.78rem]">
                <span className="text-body">By: {c.complainantName}</span>
                <span className="text-muted">Status: {c.status}</span>
              </div>
            </div>
          );
        })}
      </div>

      {selected && (
        <div className="fixed inset-0 bg-black/50 z-[1000] flex items-center justify-center p-5"
          onClick={() => setSelected(null)}>
          <div className="max-w-[700px] w-full max-h-[90vh] overflow-auto bg-white rounded-2xl p-6"
            onClick={(e) => e.stopPropagation()}>
            <div className="flex justify-between mb-4">
              <h2 className="m-0 font-serif">{selected.referenceNumber}</h2>
              <button onClick={() => setSelected(null)}
                className="bg-none border-0 text-2xl cursor-pointer text-muted">×</button>
            </div>

            <div className="text-[0.9rem] leading-relaxed mb-4">
              <div><strong>Category:</strong> {selected.categoryLabel}</div>
              <div><strong>Severity:</strong> {selected.severity}</div>
              <div><strong>Status:</strong> {selected.status}</div>
              <div><strong>By:</strong> {selected.complainantName} ({selected.complainantRole})</div>
              <div className="mt-3">
                <strong>Description:</strong>
                <div className="p-2.5 rounded-lg bg-slate-50 mt-1 whitespace-pre-wrap">
                  {selected.description}
                </div>
              </div>
              {selected.desiredOutcome && (
                <div className="mt-3">
                  <strong>Desired outcome:</strong>
                  <div className="p-2.5 rounded-lg bg-slate-50 mt-1">
                    {selected.desiredOutcome}
                  </div>
                </div>
              )}

              {selected.correspondenceLog?.length > 0 && (
                <div className="mt-3">
                  <strong>Correspondence log:</strong>
                  {selected.correspondenceLog.map((entry, i) => (
                    <div key={i} className="p-2.5 rounded-lg mt-1.5 text-[0.85rem]"
                      style={{ background: entry.direction === 'outbound' ? '#dbeafe' : '#f8fafc' }}>
                      <div className="text-[0.72rem] text-body mb-0.5">
                        {entry.direction === 'outbound' ? '↗ Sent' : '↘ Received'} by {entry.byName} ·{' '}
                        {entry.timestamp?._seconds ? new Date(entry.timestamp._seconds * 1000).toLocaleString('en-AU') : ''}
                      </div>
                      {entry.body}
                    </div>
                  ))}
                </div>
              )}
            </div>

            {selected.status === 'submitted' && (
              <button onClick={handleAck} disabled={actionInProgress}
                className="w-full p-3 rounded-[10px] border-0 bg-brand-primary text-white cursor-pointer font-bold mb-3">
                Acknowledge complaint
              </button>
            )}

            {selected.status !== 'resolved' && (
              <div className="form-group">
                <label className="form-label">Send response to complainant</label>
                <textarea className="form-input" rows={3} value={responseBody}
                  onChange={(e) => setResponseBody(e.target.value)} />
                <button onClick={handleRespond} disabled={actionInProgress || !responseBody}
                  className="mt-1.5 px-4 py-2.5 rounded-lg border-0 text-white cursor-pointer font-semibold"
                  style={{ background: '#4f46e5' }}>
                  Send response
                </button>
              </div>
            )}

            {selected.status !== 'resolved' && (
              <div className="form-group mt-4">
                <label className="form-label">Resolve with outcome statement</label>
                <textarea className="form-input" rows={3} value={resolution}
                  onChange={(e) => setResolution(e.target.value)} />
                <button onClick={handleResolve} disabled={actionInProgress || !resolution}
                  className="mt-1.5 px-4 py-2.5 rounded-lg border-0 text-white cursor-pointer font-semibold"
                  style={{ background: '#dc2626' }}>
                  Mark resolved
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}